/**
 * 아카이브 — 지난 계약서 · 제안서 · 보고서 · 견적.
 *
 * **원본은 Google Drive 에 두고 여기에는 찾을 수 있게 메타데이터만 둔다.**
 * 파일을 우리 디스크에 복사하지 않는다 — Drive 가 정본이다.
 *
 * **2026-09-28 부터 실제 DB 를 본다** — 백엔드가 이 모양으로 내준다(`archive-query.service.ts`).
 * 지금은 추출이 끝난 계약 문서만 온다. 필드 정의는 `docs/02-design/features/archive.extract-spec.md`.
 */
export interface ArchiveDoc {
  id: number;

  kind: DocKind;
  /** 문서 제목. 파일명이 아니라 사람이 읽는 이름 */
  title: string;
  /** 어느 프로젝트의 문서인가. 지난 건이라 우리 프로젝트 목록에 없을 수 있다 */
  projectName: string;
  clientName: string;
  /** 문서 시점 `YYYY-MM`. 목록 정렬과 연도 필터가 이걸 본다 */
  docMonth: string;

  /* --- AI 가 뽑는 값. 사람이 고친다 --- */
  /** 수행 기간 `YYYY-MM`. 계약서가 아니면 비어 있을 수 있다 */
  startMonth: string | null;
  endMonth: string | null;
  /** 계약금액 또는 제안가(원) */
  amount: number | null;
  manpower: { headcount: number; months: number } | null;
  /** 사업유형 — 컨설팅 · 실태조사 · 용역 같은 분류 */
  projectType: string | null;
  keywords: string[];
  /** 3~4줄 요약 */
  summary: string;

  /** 제안서·견적에만 있다. 계약서·보고서는 이미 수주한 건이라 null */
  outcome: Outcome | null;

  /* --- Drive --- */
  driveFileId: string;
  driveUrl: string;
  /** 드라이브 파일명 — 「원본 받기」가 이 이름으로 저장한다. 목데이터에는 없다 */
  fileName?: string;

  /* --- 검수 --- */
  /** AI 가 값을 뽑았나 */
  extracted: boolean;
  /** 사람이 확인했나. 확인 전 값은 화면에서 「검수 필요」로 표시한다 */
  reviewed: boolean;
  /** 추출 때 단 표시 — 날짜공란 · 금액불일치 · VAT모순 · 쪽누락 … 검수 때 먼저 본다 */
  flags?: string[];
  /** 검수 화면이 고치는 원래 값(계약서만). 목데이터에는 없다 */
  contract?: ContractFields;
  /** 필드(추출 JSON 이름) → 읽은 쪽. 누르면 원본을 그 쪽으로 연다 */
  evidence?: Record<string, number>;
  isPdf?: boolean;
}

export interface ContractFields {
  counterparty: string | null;
  endClient: string | null;
  /** YYYY-MM-DD */
  contractDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** 원 */
  amount: number | null;
  vat: '포함' | '별도' | '미기재' | null;
}

/** 검수 화면의 계약 칸 — 순서 · 이름 · 근거 쪽을 찾을 추출 JSON 키 */
export const CONTRACT_ROWS: { key: keyof ContractFields; label: string; evidence: string; type: 'text' | 'date' | 'won' | 'vat' }[] = [
  { key: 'counterparty', label: '서명 상대', evidence: 'counterparty', type: 'text' },
  { key: 'endClient', label: '최종 고객', evidence: 'end_client', type: 'text' },
  { key: 'contractDate', label: '계약일', evidence: 'contract_date', type: 'date' },
  { key: 'periodStart', label: '기간 시작', evidence: 'period_start', type: 'date' },
  { key: 'periodEnd', label: '기간 끝', evidence: 'period_end', type: 'date' },
  { key: 'amount', label: '금액', evidence: 'amount', type: 'won' },
  { key: 'vat', label: 'VAT', evidence: 'vat', type: 'vat' },
];

export const DOC_KINDS = ['계약서', '제안서', '보고서', '견적'] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export const KIND_STYLE: Record<DocKind, string> = {
  '계약서': 'bg-indigo-50 text-indigo-600',
  '제안서': 'bg-amber-50 text-amber-700',
  '보고서': 'bg-emerald-50 text-emerald-700',
  '견적': 'bg-slate-100 text-slate-600',
};

export const OUTCOMES = ['수주', '실주'] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** 연도 필터. 3년치를 나열하고 나머지는 「이전」으로 묶는다 */
export const YEAR_BUCKETS = ['2026', '2025', '2024', '이전'] as const;
export type YearBucket = (typeof YEAR_BUCKETS)[number];

export function yearBucket(docMonth: string): YearBucket {
  const y = docMonth.slice(0, 4);
  return (YEAR_BUCKETS as readonly string[]).includes(y) ? (y as YearBucket) : '이전';
}
